var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useEffect, useImperativeHandle, useRef, useState } from "react";
import { QueryBuilderContext } from "@query-builder/QueryBuilderContext";
import { QueryEditor } from "@components/QueryEditor/QueryEditor";
import { generateInitialQuery, getOutputSchemaForQuery, updateSchemasInQuery, validateParams, validateQuery } from "@utils";
import { InitialScreen } from "@components/InitialScreen/InitialScreen";
import { LoadingOverlay } from "@components/Loading/Loading";
import { checkStatus } from "@query-engine-service";
import { useQueryEngineService } from "./useQueryEngineService";
import { hasValue } from "uxp/components";
import './QueryBuilder.scss';
export const QueryBuilder = React.forwardRef((props, ref) => {
    var _a, _b, _c;
    const [query, setQuery] = useState(generateInitialQuery({
        sources: [],
        steps: []
    }));
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    // create context value 
    const context = {
        uxpContext: props.uxpContext,
        // renderCustomInput: props.renderCustomInput,
        editingQueryId: props.editingQueryId,
        externalContextDataSections: ((_a = props === null || props === void 0 ? void 0 : props.externalContextData) === null || _a === void 0 ? void 0 : _a.contextDataSections) || [],
        externalPillConfiguration: ((_b = props === null || props === void 0 ? void 0 : props.externalContextData) === null || _b === void 0 ? void 0 : _b.pillConfiguration) || {},
        canSelectSavedQuerries: (props === null || props === void 0 ? void 0 : props.canSelectSavedQuerries) || false
    };
    const viewRef = useRef(null);
    const queryEngineServices = useQueryEngineService(props.uxpContext);
    useImperativeHandle(ref, () => ({
        onSave: onBeforeSave,
        onCancel: onCancelChanges,
        getView: getView
    }));
    // on initial load check if the query engine is running
    useEffect(() => {
        checkQueryEngineStatus();
    }, []);
    useEffect(() => {
        prepareInitialQuery();
    }, [props.initialQuery]);
    useEffect(() => {
        updateViewChange();
    }, [query, error]);
    const $L = (key) => props.uxpContext.$L(key);
    function checkQueryEngineStatus() {
        return __awaiter(this, void 0, void 0, function* () {
            const { active: isRunning } = yield checkStatus(context);
            setError(isRunning ? null : $L('lqe.qb.not-available'));
        });
    }
    function prepareInitialQuery() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!(props === null || props === void 0 ? void 0 : props.initialQuery))
                return setQuery(generateInitialQuery());
            setLoading(true);
            const _query = yield updateSchemasInQuery(context, generateInitialQuery(props === null || props === void 0 ? void 0 : props.initialQuery));
            console.log('updated_initial_query', _query);
            setQuery(_query);
            // add a delay
            setTimeout(() => {
                setLoading(false);
            }, 500);
        });
    }
    function onBeforeSave() {
        return __awaiter(this, void 0, void 0, function* () {
            const { isValid, error } = validateQuery(query);
            const isParamsValid = validateParams(query);
            const schema = yield getOutputSchemaForQuery(context, query);
            if (isValid && !schema)
                console.error('Unable to get the schema');
            const isQueryValid = isValid && isParamsValid;
            const errorMessage = !isValid && hasValue(error) ? error : !isParamsValid ? $L('lqe.qb.params-error') : null;
            return {
                isValid: isQueryValid,
                query: isQueryValid ? query : null,
                schema: isQueryValid ? schema : null,
                error: isQueryValid ? null : errorMessage
            };
        });
    }
    function onCancelChanges() {
        prepareInitialQuery();
    }
    function updateViewChange() {
        var _a, _b;
        const currentView = ((_a = query === null || query === void 0 ? void 0 : query.sources) === null || _a === void 0 ? void 0 : _a.length) > 0 && !error ? 'editor' : 'home';
        if ((viewRef === null || viewRef === void 0 ? void 0 : viewRef.current) !== currentView)
            (_b = props === null || props === void 0 ? void 0 : props.onChangeView) === null || _b === void 0 ? void 0 : _b.call(props, currentView);
    }
    function getView() {
        var _a;
        if (((_a = query === null || query === void 0 ? void 0 : query.sources) === null || _a === void 0 ? void 0 : _a.length) > 0 && !error)
            return 'editor';
        else
            return 'home';
    }
    function refreshQuery(queryId) {
        return __awaiter(this, void 0, void 0, function* () {
            setLoading(true);
            // get saved query and build updated source 
            const querySource = yield queryEngineServices.getQueryDataSourceById(queryId);
            if (querySource) {
                query.sources = [querySource];
                query.steps = [];
                query.sourceOnly = true;
            }
            setQuery(Object.assign({}, query));
            setLoading(false);
        });
    }
    function renderError() {
        return React.createElement("div", { className: "lucy-query-builder-error-container-9300142b" },
            React.createElement("div", { className: "lucy-query-builder-error-messge-9300142b" }, error));
    }
    return React.createElement(QueryBuilderContext.Provider, { value: context },
        React.createElement("div", { className: "lucy-query-builder-component-container-9300142b" }, loading ? React.createElement(LoadingOverlay, { message: $L('lqe.qb.processing') }) : React.createElement(React.Fragment, null, !!error ? React.createElement(React.Fragment, null, renderError()) : React.createElement(React.Fragment, null, ((_c = query === null || query === void 0 ? void 0 : query.sources) === null || _c === void 0 ? void 0 : _c.length) == 0 ? React.createElement(InitialScreen, { onSetQuery: setQuery, query: query }) : React.createElement(QueryEditor, { query: query, onChange: setQuery, onUpdateSavedQuery: refreshQuery })))));
});
